import React from 'react';
import { AnimatedSection, StaggerChildren } from '../components/AnimatedSection';
import { TiltCard } from '../components/TiltCard';
import { useCursor } from '../context/CursorContext';
import { Logo } from '../components/Logo';
import { BrandText } from '../components/BrandText';
import { Download } from 'lucide-react';

const colors = [
  { name: 'Primary Black', hex: '#000000', tailwind: 'bg-black', textClass: 'text-white' },
  { name: 'Primary Green', hex: '#22C55E', tailwind: 'bg-green-500', textClass: 'text-black' },
  { name: 'White', hex: '#FFFFFF', tailwind: 'bg-white', textClass: 'text-black' },
  { name: 'Dark Green', hex: '#166534', tailwind: 'bg-green-800', textClass: 'text-white' },
  { name: 'Emerald', hex: '#059669', tailwind: 'bg-emerald-600', textClass: 'text-white' },
  { name: 'Teal', hex: '#14B8A6', tailwind: 'bg-teal-500', textClass: 'text-black' },
];

const typeScale = [
  { name: 'Hero Heading', size: '96px', weight: '900 (Black)', sample: 'THE MOON MEDIA', className: 'text-6xl font-black' },
  { name: 'Section Heading', size: '48px', weight: '900 (Black)', sample: 'PARTNER DEALS', className: 'text-4xl font-black' },
  { name: 'Card Heading', size: '24px', weight: '900 (Black)', sample: 'TheMoonShow', className: 'text-2xl font-black' },
  { name: 'Body Text', size: '16px', weight: '400 (Regular)', sample: 'Full creator incubation. Premium deals.', className: 'text-base' },
  { name: 'Label / Caps', size: '10-12px', weight: '600 (Semibold)', sample: 'WEB3 CREATOR MARKETING AGENCY', className: 'text-xs font-semibold uppercase tracking-[0.3em] text-green-500' },
];

const socialAssets = [
  { name: 'Twitter / X Banner', file: '/brand/twitter-banner.png', dimensions: '1500 × 500', platform: 'Twitter / X' },
  { name: 'LinkedIn Banner', file: '/brand/linkedin-banner.png', dimensions: '1584 × 396', platform: 'LinkedIn' },
  { name: 'Profile Picture', file: '/brand/profile-pic.png', dimensions: '400 × 400', platform: 'All Platforms' },
  { name: 'Instagram Profile', file: '/brand/instagram-profile.png', dimensions: '320 × 320', platform: 'Instagram' },
  { name: 'Instagram Story', file: '/brand/instagram-story.png', dimensions: '1080 × 1920', platform: 'Instagram' },
  { name: 'Partner Banner', file: '/brand/partner-banner.png', dimensions: '1920 × 1080', platform: 'Presentations' },
];

const logoVariations = [
  { name: 'On Black', file: '/brand/logo-on-black.png' },
  { name: 'On Green', file: '/brand/logo-on-green.png' },
  { name: 'On White', file: '/brand/logo-on-white.png' },
  { name: 'Transparent', file: '/brand/logo-transparent.png' },
];

const handleDownload = (filePath, fileName) => {
  const link = document.createElement('a');
  link.href = filePath;
  link.download = fileName || filePath.split('/').pop();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleDownloadAll = () => {
  const allFiles = [
    ...socialAssets.map(a => a.file),
    ...logoVariations.map(l => l.file),
    '/brand/email-signature.png',
  ];
  allFiles.forEach((file, index) => {
    setTimeout(() => handleDownload(file), index * 300);
  });
};

export default function BrandKitPage() {
  const { setLabel } = useCursor();

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <AnimatedSection className="text-center mb-20">
          <div className="flex justify-center mb-6">
            <div className="animate-float">
              <Logo size="xl" />
            </div>
          </div>
          <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">
            Official
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-3">
            BRAND KIT
          </h1>
          <p className="text-white/40 max-w-lg mx-auto mb-8">
            Everything you need to represent The Moon Media. Download assets, reference our guidelines, and maintain brand consistency.
          </p>
          <div className="mx-auto w-16 h-px bg-gradient-to-r from-transparent via-green-500 to-transparent mb-8" />
          <button
            onClick={handleDownloadAll}
            className="group relative inline-flex items-center gap-2 px-8 py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 overflow-hidden"
            onMouseEnter={() => setLabel('Download')}
            onMouseLeave={() => setLabel('')}
          >
            <Download size={16} />
            <span className="relative z-10">Download All Assets</span>
            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </AnimatedSection>

        {/* Logo Section */}
        <AnimatedSection className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 bg-green-500" />
            <h2 className="text-2xl font-black text-white uppercase">Logo</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {logoVariations.map((logo) => (
              <TiltCard key={logo.name} cursorLabel="Download" className="border border-white/5 bg-white/[0.02] hover:border-green-500/30 overflow-hidden">
                <div className="aspect-square relative">
                  <img src={logo.file} alt={logo.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <span className="text-white/60 text-sm">{logo.name}</span>
                  <button
                    onClick={() => handleDownload(logo.file, 'themoonmedia-logo-' + logo.name.toLowerCase().replace(' ', '-') + '.png')}
                    className="text-green-500 hover:text-green-400 transition-colors duration-300"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </TiltCard>
            ))}
          </div>
        </AnimatedSection>

        {/* Color Palette */}
        <AnimatedSection className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 bg-green-500" />
            <h2 className="text-2xl font-black text-white uppercase">Color Palette</h2>
          </div>
          <StaggerChildren className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" staggerDelay={80}>
            {colors.map((color) => (
              <div key={color.name} className="border border-white/5 bg-white/[0.02] overflow-hidden hover:border-green-500/30 transition-colors duration-300">
                <div className={`h-24 ${color.tailwind}`} />
                <div className="p-3">
                  <p className="text-white text-sm font-bold">{color.name}</p>
                  <p className="text-white/40 text-xs font-mono mt-1">{color.hex}</p>
                  <p className="text-white/30 text-[10px] mt-0.5">{color.tailwind}</p>
                </div>
              </div>
            ))}
          </StaggerChildren>
          <div className="mt-6 p-4 border border-white/5 bg-white/[0.02]">
            <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Opacity Variations</p>
            <div className="flex flex-wrap gap-2">
              {['text-white/60', 'text-white/40', 'text-white/30', 'border-white/10', 'border-green-500/30', 'bg-green-500/5'].map(cls => (
                <span key={cls} className="px-2 py-1 bg-white/5 text-green-500 text-[10px] font-mono">{cls}</span>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Typography */}
        <AnimatedSection className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 bg-green-500" />
            <h2 className="text-2xl font-black text-white uppercase">Typography</h2>
          </div>
          <div className="p-6 border border-white/5 bg-white/[0.02] mb-4">
            <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2">Font Family</p>
            <p className="text-white text-3xl font-black">Inter</p>
            <p className="text-white/40 text-sm mt-2">Weights: 400 (Regular) · 500 (Medium) · 600 (Semibold) · 700 (Bold) · 800 (Extrabold) · 900 (Black)</p>
          </div>
          <div className="space-y-3">
            {typeScale.map((type) => (
              <div key={type.name} className="p-4 border border-white/5 bg-white/[0.02] flex flex-col md:flex-row md:items-center gap-4">
                <div className="md:w-40 shrink-0">
                  <p className="text-green-500 text-[10px] uppercase tracking-wider">{type.name}</p>
                  <p className="text-white/30 text-xs">{type.size} · {type.weight}</p>
                </div>
                <p className={type.className + ' text-white'}>{type.sample}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Brand Text Usage */}
        <AnimatedSection className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 bg-green-500" />
            <h2 className="text-2xl font-black text-white uppercase">Brand Name</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-6 border border-green-500/30 bg-green-500/5">
              <p className="text-green-500 text-[10px] uppercase tracking-wider mb-4">Correct Usage</p>
              <div className="space-y-4">
                <div className="p-3 bg-black/40">
                  <p className="text-3xl font-black"><span className="text-white">THE </span><span className="text-green-500">MOON MEDIA</span></p>
                </div>
                <p className="text-white/40 text-sm">"THE" in white, "MOON MEDIA" in brand green. Always with spaces. Always uppercase.</p>
              </div>
            </div>
            <div className="p-6 border border-red-500/30 bg-red-500/5">
              <p className="text-red-400 text-[10px] uppercase tracking-wider mb-4">Incorrect Usage</p>
              <div className="space-y-2 opacity-50">
                <p className="text-white/60 text-lg line-through">TheMoonMedia</p>
                <p className="text-white/60 text-lg line-through">the moon media</p>
                <p className="text-white/60 text-lg line-through">THEMOONMEDIA</p>
                <p className="text-white/60 text-lg line-through">Moon Media</p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Social Media Assets */}
        <AnimatedSection className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 bg-green-500" />
            <h2 className="text-2xl font-black text-white uppercase">Social Media Assets</h2>
          </div>
          <StaggerChildren className="grid md:grid-cols-2 gap-4" staggerDelay={100}>
            {socialAssets.map((asset) => (
              <TiltCard key={asset.name} cursorLabel="Download" className="border border-white/5 bg-white/[0.02] hover:border-green-500/30 overflow-hidden">
                <div className="relative aspect-video bg-black/60">
                  <img src={asset.file} alt={asset.name} className="w-full h-full object-contain" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold text-sm">{asset.name}</p>
                    <p className="text-white/40 text-xs">{asset.dimensions} · {asset.platform}</p>
                  </div>
                  <button
                    onClick={() => handleDownload(asset.file, 'themoonmedia-' + asset.name.toLowerCase().replace(/ /g, '-') + '.png')}
                    className="flex items-center gap-1 text-green-500 text-xs font-semibold uppercase tracking-wider hover:text-green-400 transition-colors duration-300"
                  >
                    <Download size={14} />
                    <span>Download</span>
                  </button>
                </div>
              </TiltCard>
            ))}
          </StaggerChildren>
        </AnimatedSection>

        {/* Email Signature */}
        <AnimatedSection className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 bg-green-500" />
            <h2 className="text-2xl font-black text-white uppercase">Email Signature</h2>
          </div>
          <TiltCard cursorLabel="Download" className="border border-white/5 bg-white/[0.02] hover:border-green-500/30 overflow-hidden max-w-xl">
            <div className="p-4 bg-black/60">
              <img src="/brand/email-signature.png" alt="Email Signature" className="w-full" />
            </div>
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-bold text-sm">Email Signature Banner</p>
                <p className="text-white/40 text-xs">600 × 200</p>
              </div>
              <button
                onClick={() => handleDownload('/brand/email-signature.png', 'themoonmedia-email-signature.png')}
                className="flex items-center gap-1 text-green-500 text-xs font-semibold uppercase tracking-wider hover:text-green-400 transition-colors duration-300"
              >
                <Download size={14} />
                <span>Download</span>
              </button>
            </div>
          </TiltCard>
        </AnimatedSection>

        {/* Usage Guidelines */}
        <AnimatedSection className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 bg-green-500" />
            <h2 className="text-2xl font-black text-white uppercase">Usage Guidelines</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'Clear Space', desc: 'Always maintain minimum clear space around the logo equal to the height of the "M" in MOON.' },
              { title: 'Minimum Size', desc: 'The logo should never be reproduced smaller than 32px in digital or 12mm in print.' },
              { title: 'Color Integrity', desc: 'Never alter the brand green (#22C55E). On light backgrounds, use the dark logo variant.' },
              { title: 'Background', desc: 'Preferred background is pure black (#000000). Acceptable alternatives: dark gray, dark green.' },
            ].map((rule) => (
              <div key={rule.title} className="p-5 border border-white/5 bg-white/[0.02]">
                <p className="text-white font-bold text-sm mb-2">{rule.title}</p>
                <p className="text-white/40 text-sm">{rule.desc}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Footer CTA */}
        <AnimatedSection className="text-center">
          <div className="p-10 border border-green-500/20 bg-green-500/5 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-emerald-500/5 animate-gradient" />
            <div className="relative z-10">
              <p className="text-white/40 text-sm mb-2">Questions about brand usage?</p>
              <a
                href="mailto:brands@themoonmedia.com"
                className="text-green-500 font-bold text-lg hover:text-green-400 transition-colors duration-300"
                onMouseEnter={() => setLabel('Email')}
                onMouseLeave={() => setLabel('')}
              >
                brands@themoonmedia.com
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}
